import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Box, Typography, Paper, useMediaQuery } from "@mui/material";

import { useResendOtp } from "../../Hooks/useResendOtp";
import { verifyVendorOtp } from "../../Services/authService";
import setAuthorizationToken from "../../utils/setAutorization";
import ebenchLogo from "../../../src/images/ebench_logo.png";

const VotpVerify = () => {
  const location = useLocation();
  const LoginData =location.state || JSON.parse(sessionStorage.getItem("votpData"));
  const navigate = useNavigate();
  const { mutate: resendOtp } = useResendOtp();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isXS = useMediaQuery("(max-width:375px)");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [isResendVisible, setIsResendVisible] = useState(false);
  const [shake, setShake] = useState(false);

  const inputRefs = useRef([]);

  const email = LoginData?.email;
  const userType = LoginData?.userType;
  const otp = otpValues.join("");

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

   useEffect(() => {
      return () => sessionStorage.removeItem("votpData");
    }, []);

  const handleInputChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;

    const updated = [...otpValues];
    updated[index] = value;
    setOtpValues(updated);

    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      const updated = [...otpValues];
      updated[index] = "";
      setOtpValues(updated);
    }

    if (event.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (event.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleSubmit = async () => {
    if (!otp) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return toast.error("Please enter the OTP.");
    }

    try {
      await verifyVendorOtp({ code: otp, email });
      toast.success("Verification successful!");

      if (LoginData?.logIn) {
        localStorage.setItem("adminid", LoginData?.adminId);
        setAuthorizationToken(LoginData?.token);

        setTimeout(() => {
          navigate(
            LoginData?.userType === "VENDOR"
              ? "/vendor_dashboard"
              : LoginData?.userType === "ADMIN"
              ? "/admin"
              : "/candidate_dashboard"
          );
        }, 700);
      } else navigate("/login");
    } catch {
      setShake(true);
      // console.error("Invalid OTP — please try again.");
    }
  };

  useEffect(() => {
    if (secondsLeft <= 0) return setIsResendVisible(true);
    const timer = setTimeout(() => setSecondsLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const handleResendOtp = () => {
    setIsLoading(true);
    resendOtp(
      { email, userType },
      {
        onSuccess: () => {
          setSecondsLeft(60);
          setIsResendVisible(false);
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
          toast.error("Failed to resend OTP.");
        },
      }
    );
  };

  return (
    <>
      {/* HEADER */}
      <header
        className="d-flex justify-content-between align-items-center shadow-sm bg-white fixed-top"
        style={{
          padding: isMobile ? "10px 14px" : "12px 24px",
          width: "100%",
          zIndex: 10,
        }}
      >
        <Link to="/">
          <img src={ebenchLogo} alt="logo" style={{ height: isMobile ? 32 : 38 }} />
        </Link>

        <Link to="/login">
          <button
            style={{
              padding: isMobile ? "5px 12px" : "6px 18px",
              fontWeight: 600,
              borderRadius: "6px",
              border: "1px solid #286a94",
              color: "#286a94",
              background: "white",
            }}
          >
            Login
          </button>
        </Link>
      </header>

      {/* OTP UI */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#eef5ff",
          pt: 12,
          px: 2,
        }}
      >
        <Paper
          elevation={5}
          sx={{
            width: "100%",
            maxWidth: "460px",
            p: isMobile ? 3 : 4,
            borderRadius: "22px",
            textAlign: "center",
            animation: "fadeIn .4s ease",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ color: "#123b56" }}>
            Enter OTP
          </Typography>

          <Typography sx={{ color: "#5b6b76", fontSize: "14px", mt: 1, mb: 3 }}>
            We've sent a 6-digit code to your email or phone.
          </Typography>

          {/* OTP INPUTS */}
          <Box
            sx={{
              display: "flex",
              gap: isXS ? 0.5 : isMobile ? 1.2 : 2,
              justifyContent: "center",
              animation: shake ? "shake .35s" : "none",
              flexWrap: "nowrap",
            }}
          >
            {otpValues.map((v, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                maxLength={1}
                value={v}
                onChange={(e) => handleInputChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                style={{
                  width: isXS ? 40 : isMobile ? 48 : 58,
                  height: isXS ? 48 : isMobile ? 55 : 62,
                  textAlign: "center",
                  fontSize: isXS ? 20 : 22,
                  fontWeight: "600",
                  borderRadius: "10px",
                  border: "2px solid #cbd6e3",
                  outline: "none",
                  transition: ".2s",
                }}
              />
            ))}
          </Box>

          {/* Submit */}
          <Button
            fullWidth
            onClick={handleSubmit}
            variant="contained"
            sx={{
              mt: 4,
              py: 1.3,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              background: "linear-gradient(135deg,#286a94,#1e4e70)",
              fontSize: isXS ? "14px" : "16px",
            }}
          >
            Verify OTP
          </Button>

          {/* Resend */}
          <Box mt={3}>
            {isResendVisible ? (
              <>
                <Typography sx={{ mb: 1, color: "#5b6b76" }}>
                  Didn't receive the code?
                </Typography>
                <Button
                  variant="outlined"
                  disabled={isLoading}
                  onClick={handleResendOtp}
                  sx={{
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#286a94",
                    color: "#286a94",
                    borderRadius: "10px",
                  }}
                >
                  {isLoading ? "Sending..." : "Resend OTP"}
                </Button>
              </>
            ) : (
              <Typography sx={{ color: "#286a94", fontWeight: 600 }}>
                Resend available in {secondsLeft}s
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Animations */}
      <style>
        {`
          @keyframes shake {
            0%,100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            50% { transform: translateX(5px); }
            75% { transform: translateX(-5px); }
          }
          @keyframes fadeIn {
            from { opacity:0; transform: translateY(10px); }
            to { opacity:1; transform: translateY(0); }
          }
        `}
      </style>
    </>
  );
};

export default VotpVerify;
